'use client';

import React, { memo, useMemo } from 'react';
import AppIcon from './AppIcon';
import AppImage from './AppImage';

interface AppLogoProps {
  src?: string; // Image source (optional, used as light mode logo)
  iconName?: string; // Icon name when no image
  size?: number; // Size for icon/image
  className?: string; // Additional classes
  onClick?: () => void; // Click handler
}

const LIGHT_LOGO = '/assets/images/88fae7b0-4ec9-4e2a-a86b-425065ffdbe7-1786380439194.png';
const DARK_LOGO = '/assets/images/88fae7b0-4ec9-4e2a-a86b-425065ffdbe7__1_-1786380439065.png';

const AppLogo = memo(function AppLogo({
  src = LIGHT_LOGO,
  iconName = 'SparklesIcon',
  size = 80,
  className = '',
  onClick,
}: AppLogoProps) {
  const containerClassName = useMemo(() => {
    const classes = ['flex items-center'];
    if (onClick) classes.push('cursor-pointer hover:opacity-80 transition-opacity');
    if (className) classes.push(className);
    return classes.join(' ');
  }, [onClick, className]);

  return (
    <div className={containerClassName} onClick={onClick}>
      {src ? (
        <>
          {/* Light mode logo — hidden in dark mode */}
          <div className="block dark:hidden">
            <AppImage
              src={src}
              alt="Logo"
              width={size}
              height={size}
              className="flex-shrink-0"
              priority={true}
              unoptimized={src.endsWith('.svg')}
            />
          </div>
          {/* Dark mode logo — hidden in light mode */}
          <div className="hidden dark:block">
            <AppImage
              src={DARK_LOGO}
              alt="Logo"
              width={size}
              height={size}
              className="flex-shrink-0"
              priority={true}
              unoptimized={DARK_LOGO.endsWith('.svg')}
            />
          </div>
        </>
      ) : (
        <AppIcon name={iconName} size={size} className="flex-shrink-0" />
      )}
    </div>
  );
});

export default AppLogo;
